/**
 * Gmail API service for fetching and sending emails
 */

import { GmailOAuth, GmailTokens } from './GmailOAuth';

export interface GmailMessage {
  id: string;
  threadId: string;
  labelIds: string[];
  snippet: string;
  payload: {
    headers: { name: string; value: string }[];
    body?: { data?: string; size: number };
    parts?: any[];
  };
  internalDate: string;
}

export interface GmailThread {
  id: string;
  snippet: string;
  historyId: string;
  messages: GmailMessage[];
}

export interface ParsedEmail {
  id: string;
  threadId: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  date: Date;
  isRead: boolean;
}

export class GmailAPI {
  private oauth: GmailOAuth;
  private tokens: GmailTokens | null = null;

  constructor() {
    this.oauth = new GmailOAuth();
    this.tokens = this.oauth.loadTokens();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.tokens !== null;
  }

  /**
   * Start OAuth flow
   */
  startAuth(): void {
    window.location.href = this.oauth.getAuthUrl();
  }

  /**
   * Complete OAuth flow with authorization code
   */
  async completeAuth(code: string): Promise<void> {
    this.tokens = await this.oauth.exchangeCodeForTokens(code);
    this.oauth.saveTokens(this.tokens);
  }

  /**
   * Sign out and clear tokens
   */
  async signOut(): Promise<void> {
    if (this.tokens) {
      await this.oauth.revokeToken(this.tokens.access_token);
    }
    this.oauth.clearTokens();
    this.tokens = null;
  }

  /**
   * Get valid access token
   */
  private async getAccessToken(): Promise<string> {
    if (!this.tokens) {
      throw new Error('Not authenticated');
    }
    return this.oauth.getValidAccessToken(this.tokens);
  }

  /**
   * Make authenticated Gmail API request
   */
  private async apiRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = await this.getAccessToken();

    const response = await fetch(`https://gmail.googleapis.com/gmail/v1${endpoint}`, {
      ...options,
      headers: {
        ...options?.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Gmail API error: ${error}`);
    }

    return response.json();
  }

  /**
   * Get user's Gmail profile
   */
  async getProfile(): Promise<any> {
    return this.apiRequest('/users/me/profile');
  }

  /**
   * List messages with optional query
   */
  async listMessages(maxResults: number = 50, query?: string): Promise<{ messages: { id: string; threadId: string }[] }> {
    const params = new URLSearchParams({
      maxResults: String(maxResults)
    });

    if (query) {
      params.set('q', query);
    }

    return this.apiRequest(`/users/me/messages?${params.toString()}`);
  }

  /**
   * Get a single message by ID
   */
  async getMessage(messageId: string): Promise<GmailMessage> {
    return this.apiRequest(`/users/me/messages/${messageId}?format=full`);
  }

  /**
   * Get a thread with all messages
   */
  async getThread(threadId: string): Promise<GmailThread> {
    return this.apiRequest(`/users/me/threads/${threadId}?format=full`);
  }

  /**
   * Parse Gmail message headers and body
   */
  parseMessage(message: GmailMessage): ParsedEmail {
    const headers = message.payload.headers;
    const getHeader = (name: string) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';

    // Decode base64url body
    let body = '';
    if (message.payload.body?.data) {
      body = this.decodeBase64Url(message.payload.body.data);
    } else if (message.payload.parts) {
      // Multi-part message, find text/plain or text/html
      const textPart = message.payload.parts.find(p => p.mimeType === 'text/plain' || p.mimeType === 'text/html');
      if (textPart?.body?.data) {
        body = this.decodeBase64Url(textPart.body.data);
      }
    }

    return {
      id: message.id,
      threadId: message.threadId,
      from: getHeader('From'),
      to: getHeader('To'),
      subject: getHeader('Subject'),
      body: body || message.snippet,
      date: new Date(parseInt(message.internalDate)),
      isRead: !message.labelIds.includes('UNREAD')
    };
  }

  /**
   * Decode base64url string
   */
  private decodeBase64Url(data: string): string {
    // Convert base64url to base64
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    try {
      return atob(base64);
    } catch {
      return data;
    }
  }

  /**
   * Fetch recent conversations (threads) from Gmail
   */
  async fetchRecentConversations(maxResults: number = 20): Promise<ParsedEmail[][]> {
    const { messages } = await this.listMessages(maxResults);

    if (!messages || messages.length === 0) {
      return [];
    }

    // Group by thread
    const threadMap = new Map<string, ParsedEmail[]>();

    for (const msg of messages) {
      const fullMessage = await this.getMessage(msg.id);
      const parsed = this.parseMessage(fullMessage);

      if (!threadMap.has(parsed.threadId)) {
        threadMap.set(parsed.threadId, []);
      }
      threadMap.get(parsed.threadId)!.push(parsed);
    }

    // Convert to array of conversations
    return Array.from(threadMap.values()).map(thread =>
      thread.sort((a, b) => a.date.getTime() - b.date.getTime())
    );
  }

  /**
   * Send email
   */
  async sendMessage(to: string, subject: string, body: string): Promise<GmailMessage> {
    const email = [
      `To: ${to}`,
      `Subject: ${subject}`,
      '',
      body
    ].join('\r\n');

    const encodedEmail = btoa(email).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    return this.apiRequest('/users/me/messages/send', {
      method: 'POST',
      body: JSON.stringify({ raw: encodedEmail })
    });
  }
}
